import DigitalPair from "../components/ui/DigitalPair"
import {StringPairFilter} from "../components/ui/AgStringPairFilter"
import Pair from "../components/ui/Pair"

export class GridUtil {

  constructor(agGrid) {
    if (agGrid) {
      this.columnApi = agGrid.columnApi
      this.gridApi = agGrid.api
    }
  }

  static stringPairComparator(pair1, pair2, node1, node2, isInverted) {
    const first1 = pair1 && (pair1.first || pair1.props && pair1.props.first)
    const first2 = pair2 && (pair2.first || pair2.props && pair2.props.first)
    const comparison = GridUtil.genericCompareBlankAlwaysLast(first1, first2, null, null, isInverted)
    if (comparison !== 0) {
      return comparison
    }
    const second1 = pair1 && (pair1.second || pair1.props && pair1.props.second)
    const second2 = pair2 && (pair2.second || pair2.props && pair2.props.second)
    return GridUtil.genericCompareBlankAlwaysLast(second1, second2, null, null, isInverted)
  }

  static genericCompareBlankAlwaysLast(obj1, obj2, node1, node2, isInverted) {
    if (!obj1) {
      return !obj2 ? 0 : isInverted ? -1 : 1
    }
    if (!obj2) {
      return isInverted ? 1 : -1
    }
    return obj1 < obj2 ? -1 : obj1 > obj2 ? 1 : 0
  }

  getAllColumns() {
    return this.columnApi.getAllColumns()
  }

  /* utility functions for Grid sorting and filters - any component or part of any component may be null or undefined */

  getHeaderNames() {
    return this.getAllColumns().map((col) => col.colDef.headerName)
  }

  getFirstSelectedRow() {
    const selectedRows = this.gridApi.getSelectedRows()
    return selectedRows.length ? selectedRows[0] : null
  }

  getAllRows() {
    return this.getAllRowNodes().map((row) => row.data)
  }

  getAllRowNodes() {
    return this.gridApi.getModel().rowsToDisplay
  }

  getFilteredRows() {
    const filteredRows = []
    this.gridApi.forEachNodeAfterFilterAndSort(function (rowNode) {
      filteredRows.push(rowNode.data)
    })
    return filteredRows
  }

  getFilterLog() {
    return 'Filter: ' + JSON.stringify(this.gridApi.getFilterModel())
  }

  getFormattedValue(columnName, rowIndex) {
    return this.columnApi.getColumn(columnName).colDef.cellRenderer(
        this.gridApi.getModel().getRowNode(rowIndex))
  }

  forceRefreshNode(node) {
    this.gridApi.refreshCells({
      rowNodes: [node],
      force: true
    })
  }

  smartSizeColumns(containerEl, options = {}) {
    if (!containerEl) {
      return
    }
    const {paddingOffset = 0, fixedColIds = [], minColWidth = 0, initialColWidths = {}} = options
    const fixedSet = new Set(fixedColIds)

    this.columnApi.autoSizeAllColumns(true)

    const cols = this.columnApi.getAllDisplayedColumns()
    const minWidths = cols.map(col => {
      const colId = col.getColId()
      if (initialColWidths[colId] !== undefined) {
        return initialColWidths[colId]
      }
      const measured = col.getActualWidth()
      if (fixedSet.has(colId) || minColWidth === 0) {
        return measured
      }
      return Math.max(measured, minColWidth)
    })
    const totalMin = minWidths.reduce((sum, w) => sum + w, 0)
    const viewport = containerEl.querySelector('.ag-center-cols-viewport')
    const available = (viewport ? viewport.clientWidth : containerEl.clientWidth) - paddingOffset

    if (totalMin < available) {
      const surplus = available - totalMin
      const growableCols = fixedSet.size > 0 ? cols.filter(col => !fixedSet.has(col.getColId())) : cols
      const growableTotal = growableCols.reduce((sum, col) => sum + minWidths[cols.indexOf(col)], 0)
      this.columnApi.setColumnWidths(
        cols.map((col, i) => {
          if (fixedSet.has(col.getColId())) {
            return {key: col, newWidth: minWidths[i]}
          }
          return {
            key: col,
            newWidth: Math.floor(minWidths[i] + (minWidths[i] / growableTotal) * surplus)
          }
        })
      )
    } else {
      this.columnApi.setColumnWidths(
        cols.map((col, i) => ({key: col, newWidth: minWidths[i]}))
      )
    }

    requestAnimationFrame(() => this.updateHeaderHeight(containerEl))
  }

  updateHeaderHeight(containerEl) {
    if (!containerEl) {
      return
    }
    const headerTexts = Array.from(containerEl.querySelectorAll('.ag-header-cell-text'))
    headerTexts.forEach(el => { el.style.alignSelf = 'flex-start' })
    const maxHeight = headerTexts.reduce((max, el) => Math.max(max, el.offsetHeight), 0)
    headerTexts.forEach(el => { el.style.alignSelf = '' })
    this.gridApi.setHeaderHeight(maxHeight + 4)
  }
}

export const genericStringPairProperties = {
  cellRendererFramework: Pair,
  filter: StringPairFilter,
  comparator: GridUtil.stringPairComparator
}

export const mocGenericStringPairProperties = {
  cellRendererFramework: DigitalPair,
  filter: StringPairFilter,
  comparator: GridUtil.stringPairComparator
}
